import { useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Table, Tabs, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Ban, BookText, Building2, FileCheck2, FileWarning, Hourglass, Send } from 'lucide-react';
import Layout from '../../Layout';
import { ktApi, today, useKt } from './ktStore';
import {
  billTotal, depositBill, depositsFor, dmy, fmtMoney, invoiceAmount, invoiceCheck, invoiceQueue, invoiceTax, openDeposits, PAY_METHOD,
  type Bill, type Buyer, type Payment,
} from './ktLogic';
import { DemoBar, InvoiceTag, KIND_LABEL, Kpi, PageHeader, Panel, showErrors, SourceTag } from './ktUi';

interface CashRow extends Payment { billNo: string; billDate: string; customer: string; invoiceNo?: string }

/** Hóa đơn đầu ra (SRS 1.3): chặn lỗi trước khi phát hành, gộp cọc, ghi ngược số hóa đơn, báo điều chỉnh. Phát hành là giả lập EasyInvoice. */
export default function KeToanInvoices() {
  const s = useKt();
  const now = today();
  const [picked, setPicked] = useState<string[]>([]);
  const [buyerOf, setBuyerOf] = useState<Bill | null>(null);
  const queue = useMemo(() => [...invoiceQueue(s)].sort((a, b) => b.seq - a.seq), [s]);
  const issued = useMemo(() => s.bills.filter(b => ['issued', 'adjusted', 'replaced'].includes(b.invoice.status)).sort((a, b) => (b.invoice.at || 0) - (a.invoice.at || 0)), [s.bills]);
  const adjust = s.bills.filter(b => b.invoice.status === 'needs_adjust');
  const deposits = openDeposits(s);
  const blocked = queue.filter(b => invoiceCheck(s, b).errors.length);
  const cash = useMemo<CashRow[]>(() => s.bills.filter(b => b.status === 'valid')
    .flatMap(b => b.payments.map(p => ({ ...p, billNo: b.no, billDate: p.date || b.date, customer: b.customer, invoiceNo: b.invoice.no })))
    .sort((a, b) => b.billDate.localeCompare(a.billDate) || b.billNo.localeCompare(a.billNo)), [s.bills]);

  const who: ColumnsType<Bill> = [
    { title: 'Bill', key: 'no', width: 150, render: (_, b) => <div><span className="font-medium">{b.no}</span><div><SourceTag bill={b} /></div></div> },
    { title: 'Ngày', key: 'd', width: 106, render: (_, b) => dmy(b.date) },
    { title: 'Khách', key: 'c', width: 170, render: (_, b) => (
      <div>
        <div>{b.customer}</div>
        {b.buyer ? <div className="text-[11px] text-sky-700">{b.buyer.name}{b.buyer.taxCode ? ` · MST ${b.buyer.taxCode}` : ''}</div> : <div className="text-[11px] text-gray-400">Khách lẻ</div>}
      </div>
    ) },
  ];

  const queueCols: ColumnsType<Bill> = [
    ...who,
    { title: 'Nội dung', key: 'n', render: (_, b) => {
      const dep = depositBill(s, b);
      return (
        <div>
          <div>{KIND_LABEL[b.kind]}: {b.lines.map(l => l.name).join(', ')}</div>
          {dep && <div className="text-[11px] text-sky-700">Gộp cọc {dep.no} ngày {dmy(dep.date)}: {fmtMoney(billTotal(dep))} + thu thêm {fmtMoney(billTotal(b))}</div>}
        </div>
      );
    } },
    { title: 'Giá trị hóa đơn', key: 'a', align: 'right', width: 150, render: (_, b) => {
      const t = invoiceTax(s, b);
      return <div><b>{fmtMoney(invoiceAmount(s, b))}</b><div className="text-[11px] text-gray-500">VAT {fmtMoney(t.vat)}</div></div>;
    } },
    { title: 'Kiểm tra', key: 'c', width: 310, render: (_, b) => {
      const c = invoiceCheck(s, b, now);
      if (!c.errors.length && !c.warnings.length) return <span className="text-xs text-emerald-700">Đủ điều kiện</span>;
      return (
        <div className="space-y-0.5">
          {c.errors.map(e => <div key={e} className="text-xs text-rose-600 font-medium">{e}</div>)}
          {c.warnings.map(e => <div key={e} className="text-xs text-amber-700">{e}</div>)}
        </div>
      );
    } },
    { title: '', key: 'x', width: 130, align: 'right', render: (_, b) => {
      const d = depositsFor(s, b)[0];
      return (
        <div className="flex flex-col items-end gap-1">
          <Tooltip title="Ghi thông tin đơn vị mua hàng"><Button size="small" icon={<Building2 size={13} />} aria-label={`Người mua ${b.no}`} onClick={() => setBuyerOf(b)}>Người mua</Button></Tooltip>
          {d && <Button size="small" onClick={() => { const r = ktApi.mergeDeposit(b.id, d.id); r.ok ? message.success(`Đã gộp cọc ${d.no}`) : showErrors(r.errors); }}>Gộp cọc</Button>}
        </div>
      );
    } },
  ];

  const issuedCols: ColumnsType<Bill> = [
    { title: 'Số hóa đơn', key: 'no', width: 130, render: (_, b) => <div><span className="font-medium">{b.invoice.no}</span><div className="text-[11px] text-gray-500">{b.invoice.at ? dayjs(b.invoice.at).format('DD/MM/YYYY') : ''}</div></div> },
    ...who,
    { title: 'Chưa VAT', key: 'net', align: 'right', width: 130, render: (_, b) => fmtMoney(invoiceTax(s, b).net) },
    { title: 'VAT', key: 'vat', align: 'right', width: 120, render: (_, b) => fmtMoney(invoiceTax(s, b).vat) },
    { title: 'Trạng thái', key: 's', width: 200, render: (_, b) => (
      <div>
        <InvoiceTag status={b.invoice.status} />
        {b.invoice.adjustNo && <div className="text-[11px] text-gray-500 mt-0.5">Hóa đơn điều chỉnh {b.invoice.adjustNo}</div>}
        {b.invoice.replaceNo && <div className="text-[11px] text-gray-500 mt-0.5">Hóa đơn thay thế {b.invoice.replaceNo}</div>}
      </div>
    ) },
  ];

  const fix = (b: Bill, kind: 'adjust' | 'replace') => {
    const r = ktApi.fixInvoice(b.id, kind);
    r.ok ? message.success(`Đã lập hóa đơn ${kind === 'adjust' ? 'điều chỉnh' : 'thay thế'} (giả lập)`) : showErrors(r.errors);
  };
  const adjustCols: ColumnsType<Bill> = [
    { title: 'Số hóa đơn', key: 'no', width: 130, render: (_, b) => <span className="font-medium">{b.invoice.no}</span> },
    ...who,
    { title: 'Thay đổi', key: 'l', render: (_, b) => <div className="text-xs">{b.invoice.flagReason || 'Bill đã thay đổi sau khi xuất hóa đơn'}</div> },
    { title: '', key: 'a', width: 230, align: 'right', render: (_, b) => (
      <div className="flex justify-end gap-2">
        <Button size="small" type="primary" onClick={() => fix(b, 'adjust')}>Điều chỉnh</Button>
        <Button size="small" onClick={() => fix(b, 'replace')}>Thay thế</Button>
      </div>
    ) },
  ];

  const depositCols: ColumnsType<Bill> = [
    ...who,
    { title: 'SĐT', dataIndex: 'phone', width: 120 },
    { title: 'Dịch vụ cọc', key: 'n', render: (_, b) => b.lines.map(l => l.name).join(', ') },
    { title: 'Tiền cọc', key: 'a', align: 'right', width: 140, render: (_, b) => <b>{fmtMoney(billTotal(b))}</b> },
  ];

  const cashCols: ColumnsType<CashRow> = [
    { title: 'Ngày thu', key: 'd', width: 110, render: (_, r) => dmy(r.billDate) },
    { title: 'Bill', dataIndex: 'billNo', width: 100 },
    { title: 'Khách', dataIndex: 'customer' },
    { title: 'Hình thức', key: 'm', width: 170, render: (_, r) => PAY_METHOD[r.method] },
    { title: 'Mã giao dịch', dataIndex: 'ref', width: 170 },
    { title: 'Số tiền', key: 'a', align: 'right', width: 140, render: (_, r) => fmtMoney(r.amount) },
    { title: 'Số hóa đơn', key: 'i', width: 130, render: (_, r) => r.invoiceNo || <span className="text-gray-400">Chưa xuất</span> },
  ];

  const issue = () => {
    const r = ktApi.issueInvoices(picked);
    if (r.ok) { message.success(`Đã phát hành ${picked.length} hóa đơn (giả lập)`); setPicked([]); } else showErrors(r.errors);
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Hóa đơn đầu ra" desc="Bill từ POS tự vào hàng chờ; lỗi bị chặn trước khi phát hành; số hóa đơn ghi ngược về bill và sổ quỹ" />
        <DemoBar />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<Send size={20} />} tone="bg-sky-50 text-sky-600" label="Chờ phát hành" value={queue.length} hint={`${blocked.length} bill đang bị chặn`} />
          <Kpi icon={<FileCheck2 size={20} />} tone="bg-emerald-50 text-emerald-600" label="Đã xuất" value={issued.length} />
          <Kpi icon={<FileWarning size={20} />} tone="bg-rose-50 text-rose-600" label="Cần điều chỉnh" value={adjust.length} hint="Bill đã xuất hóa đơn rồi bị sửa, hủy hoặc hoàn tiền" />
          <Kpi icon={<Hourglass size={20} />} tone="bg-amber-50 text-amber-600" label="Cọc chờ dùng dịch vụ" value={deposits.length} hint={fmtMoney(deposits.reduce((a, b) => a + billTotal(b), 0))} />
        </div>

        <Tabs items={[
          { key: 'queue', label: `Chờ phát hành (${queue.length})`, children: (
            <div className="flex flex-col gap-3">
              <Alert type="info" showIcon message="Mọi bill bán hàng đều vào hàng chờ. Khách không cần hóa đơn đích danh thì xuất theo khách lẻ; cần cho đơn vị thì bấm Người mua để ghi mã số thuế." />
              <Panel title="Bill chờ phát hành" extra={(
                <Button type="primary" size="small" icon={<Send size={13} />} disabled={!picked.length} onClick={issue}>Phát hành {picked.length ? `${picked.length} hóa đơn` : ''}</Button>
              )}>
                <Table rowKey="id" size="middle" columns={queueCols} dataSource={queue} pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }} scroll={{ x: 1250 }} locale={{ emptyText: 'Không còn bill chờ phát hành' }}
                  rowSelection={{
                    selectedRowKeys: picked,
                    onChange: k => setPicked(k as string[]),
                    getCheckboxProps: b => ({ disabled: invoiceCheck(s, b).errors.length > 0, 'aria-label': `Chọn ${b.no}` }),
                  }} />
              </Panel>
            </div>
          ) },
          { key: 'adjust', label: `Cần điều chỉnh (${adjust.length})`, children: (
            <div className="flex flex-col gap-3">
              <Alert type="info" showIcon message="Hóa đơn đã xuất không hủy được theo Nghị định 70/2025, chỉ xử lý bằng hóa đơn điều chỉnh hoặc thay thế. Kế toán thuế chọn cách phù hợp từng trường hợp." />
              <Panel title="Hóa đơn cần điều chỉnh" icon={<Ban size={15} className="text-rose-600" />}>
                <Table rowKey="id" size="middle" columns={adjustCols} dataSource={adjust} pagination={false} scroll={{ x: 950 }} locale={{ emptyText: 'Không có hóa đơn cần điều chỉnh' }} />
              </Panel>
            </div>
          ) },
          { key: 'issued', label: `Đã xuất (${issued.length})`, children: (
            <Panel title="Hóa đơn đã xuất">
              <Table rowKey="id" size="middle" columns={issuedCols} dataSource={issued} pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }} scroll={{ x: 950 }} locale={{ emptyText: 'Chưa có hóa đơn' }} />
            </Panel>
          ) },
          { key: 'cash', label: 'Sổ quỹ', children: (
            <Panel title="Sổ quỹ theo khoản thu" icon={<BookText size={15} className="text-sky-600" />}>
              <Table rowKey="id" size="small" columns={cashCols} dataSource={cash} pagination={{ pageSize: 15, showSizeChanger: false }} scroll={{ x: 950 }} />
            </Panel>
          ) },
          { key: 'deposit', label: `Cọc chờ dùng (${deposits.length})`, children: (
            <div className="flex flex-col gap-3">
              <Alert type="info" showIcon message="Hệ thống dò cọc theo SĐT và gợi ý gộp ở hàng chờ. Thời điểm xuất với cọc và thẻ liệu trình chờ Kế toán trưởng chốt." />
              <Panel title="Đơn cọc chưa dùng">
                <Table rowKey="id" size="middle" columns={depositCols} dataSource={deposits} pagination={false} scroll={{ x: 800 }} locale={{ emptyText: 'Không có cọc đang chờ' }} />
              </Panel>
            </div>
          ) },
        ]} />
      </div>
      {buyerOf && <BuyerModal bill={buyerOf} onClose={() => setBuyerOf(null)} />}
    </Layout>
  );
}

function BuyerModal({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const [f, setF] = useState<Buyer>(bill.buyer || { name: '', taxCode: '', address: '', email: '' });
  const up = (p: Partial<Buyer>) => setF(x => ({ ...x, ...p }));
  const save = (buyer?: Buyer) => { const r = ktApi.setBuyer(bill.id, buyer); if (r.ok) { message.success('Đã lưu người mua'); onClose(); } else showErrors(r.errors); };
  return (
    <Modal open title={`Người mua trên hóa đơn ${bill.no}`} cancelText="Thôi" onCancel={onClose} destroyOnHidden okText="Lưu" okButtonProps={{ disabled: !f.name.trim() }} onOk={() => save(f)}>
      <div className="space-y-3">
        <div><label htmlFor="kt-b-name" className="text-sm font-medium">Tên đơn vị</label><Input id="kt-b-name" value={f.name} onChange={e => up({ name: e.target.value })} /></div>
        <div><label htmlFor="kt-b-tax" className="text-sm font-medium">Mã số thuế</label><Input id="kt-b-tax" value={f.taxCode} onChange={e => up({ taxCode: e.target.value })} /></div>
        <div><label htmlFor="kt-b-addr" className="text-sm font-medium">Địa chỉ</label><Input id="kt-b-addr" value={f.address} onChange={e => up({ address: e.target.value })} /></div>
        <div><label htmlFor="kt-b-mail" className="text-sm font-medium">Email nhận hóa đơn</label><Input id="kt-b-mail" value={f.email} onChange={e => up({ email: e.target.value })} /></div>
        {bill.buyer && <Button size="small" onClick={() => save(undefined)}>Bỏ thông tin, xuất theo khách lẻ</Button>}
      </div>
    </Modal>
  );
}
